/**
 * The session: who is signed in, what countries they may enter, and what they may
 * do inside each one.
 *
 * ===========================================================================
 * THE ACCESSIBLE-COUNTRY SET MIRRORS THE API'S, EXACTLY
 * ===========================================================================
 * `GET /me` returns `{grp, scopes:[{c,r}], perms:{CC:[keys]}}`. The API decides
 * which countries a caller is inside with `tokenCountryCodes()`
 * (`apps/api/src/common/security/token-payload.ts`): the UNION of `scopes`
 * country codes and the KEYS of `perms`, with `grp:true` meaning every country.
 *
 * This file reproduces that union deliberately, and the second half of it is the
 * part worth stating: a per-user override can grant a permission in a country
 * where the user holds no ROLE. That grant is real — the API will honour it — so a
 * picker built from `scopes` alone would hide a country the user can actually use,
 * and the user would be stuck with no way in. Reproducing the union means the
 * picker can never show FEWER countries than the API allows, and — because it is
 * the same union, not a looser one — never more.
 *
 * The three branches the brief requires fall out of that one set:
 *   · 0 countries and not `grp` → the no-access state;
 *   · exactly 1               → skip the picker entirely and enter;
 *   · `grp`                   → all five, plus the in-app switcher.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  clearTokens,
  fetchMe,
  hasStoredSession,
  login as apiLogin,
  logout as apiLogout,
  restoreSession,
  setPasswordChangeRequiredListener,
  setPremisesRestrictedListener,
  setSessionExpiredListener,
} from '../lib/api';
import type { MeResponse } from '../lib/api';
import type { PermissionKey } from '../lib/permissions';
import { COUNTRY_ORDER, isCountryCode } from '../domain/countries';
import type { CountryCode } from '../domain/countries';
import { PasswordChangeRequiredScreen } from './PasswordChangeRequiredScreen';
import { PremisesBlockedScreen } from './PremisesBlockedScreen';

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export interface Session {
  status: SessionStatus;
  me: MeResponse | null;
  /** Countries this user may enter, in the canonical display order. */
  accessibleCountries: readonly CountryCode[];
  isGroupAdmin: boolean;
  /**
   * Design U8 item 11 (D16): this session was minted with the must-change claim — from
   * `GET /me`'s `must_change_password`, or from a 403 `PASSWORD_CHANGE_REQUIRED` that
   * arrived mid-session. While true the provider renders the password screen instead of
   * the app; `completePasswordChange` is the only thing that clears it.
   */
  mustChangePassword: boolean;
  /** Permission keys held in `code`. `grp:true` short-circuits to "all". */
  permsIn: (code: CountryCode) => readonly PermissionKey[];
  can: (code: CountryCode, key: PermissionKey) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * The password screen's exit: the server accepted `newPassword` (and revoked every
   * refresh token), so sign the same person straight back in with it and re-read `/me`,
   * whose fresh token carries no claim. Throws if the re-login fails — the screen shows it.
   */
  completePasswordChange: (newPassword: string) => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

/** The union the API itself computes — see the file header. */
export function accessibleCountriesOf(me: MeResponse | null): readonly CountryCode[] {
  if (me === null) return [];
  if (me.grp) return COUNTRY_ORDER;
  const codes = new Set<CountryCode>();
  for (const scope of me.scopes) {
    if (isCountryCode(scope.c)) codes.add(scope.c);
  }
  for (const code of Object.keys(me.perms)) {
    if (isCountryCode(code)) codes.add(code);
  }
  // Canonical order, not insertion order: the picker's tile order must be stable
  // across sessions and independent of how the API happened to serialise scopes.
  return COUNTRY_ORDER.filter((code) => codes.has(code));
}

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<SessionStatus>(() =>
    hasStoredSession() ? 'loading' : 'anonymous',
  );
  const [me, setMe] = useState<MeResponse | null>(null);

  /**
   * D7 (Phase 2.5b): true after any response answered 403 `PREMISES_RESTRICTED`.
   * While true the provider renders the blocked screen INSTEAD of its children —
   * the whole app unmounts, every query unsubscribes, and nothing under the
   * router can fire the next request that would be refused the same way. That
   * unmount is the "must not loop" guarantee: the blocked screen is static JSX
   * with no queries of its own, and repeated notifications while it shows are
   * `setState(true)` over `true` — a React bail-out, not a render.
   */
  const [premisesBlocked, setPremisesBlocked] = useState(false);

  /**
   * Design U8 item 11 (D16): true while this session must change its password. Set from
   * `GET /me` on every identity read (boot, sign-in, the re-login after a change) and by
   * the api client's listener when a 403 `PASSWORD_CHANGE_REQUIRED` arrives mid-session.
   * While true the provider renders the password screen INSTEAD of its children — the
   * same unmount-the-app shape as the premises block, for the same "must not loop" reason:
   * nothing under the router can fire the next request the gate would refuse, and the
   * screen's own two calls (`PATCH /me/password`, `POST /auth/logout`) are the ones the
   * gate admits.
   */
  const [mustChangePassword, setMustChangePassword] = useState(false);

  /**
   * The API client calls this when a refresh fails — the chain is gone, whatever
   * the reason. Everything local is dropped and the app falls back to `anonymous`,
   * which the route guard turns into the login screen. No message is invented
   * about WHY, because the client genuinely does not know (expired vs revoked vs
   * revoke-on-rotate look identical from here, on purpose).
   *
   * A dead session also clears the premises block: an anonymous user cannot be
   * off-premises (the guard passes requests with no JWT — it bites on use), and
   * leaving the flag up would show the blocked screen over the login form. The
   * must-change flag clears for the same reason: it is a property of a session.
   */
  useEffect(() => {
    setSessionExpiredListener(() => {
      setMe(null);
      setStatus('anonymous');
      setPremisesBlocked(false);
      setMustChangePassword(false);
    });
    return () => setSessionExpiredListener(null);
  }, []);

  /**
   * The premises listener — same registration pattern as the session-expired
   * one, one line above, and owned here for the same reason: the refusal is a
   * whole-session state, and the session provider is the one component that can
   * swap the whole app for a screen.
   */
  useEffect(() => {
    setPremisesRestrictedListener(() => {
      setPremisesBlocked(true);
    });
    return () => setPremisesRestrictedListener(null);
  }, []);

  /**
   * The must-change listener (design U8 / D16) — the third registration of the same
   * shape. It covers the session that gained the claim AFTER this page read `/me`: a
   * refresh in another tab, a password that crossed the 90-day line mid-session.
   */
  useEffect(() => {
    setPasswordChangeRequiredListener(() => {
      setMustChangePassword(true);
    });
    return () => setPasswordChangeRequiredListener(null);
  }, []);

  /**
   * Boot: rebuild the session from the stored refresh token, then load identity.
   *
   * =========================================================================
   * DO NOT ADD A "RUN ONCE" REF GUARD HERE. ONE WAS HERE, AND IT WEDGED THE APP.
   * =========================================================================
   * The obvious optimisation — a `useRef` flag so StrictMode's double invocation
   * only spends one refresh — is a DEADLOCK in combination with the `cancelled`
   * cleanup, and it shipped a broken reload that no unit test caught. The
   * sequence, observed in a real browser:
   *
   *   1. effect run #1 starts the async chain and returns its cleanup;
   *   2. StrictMode immediately runs that cleanup → `cancelled = true`, so chain #1
   *      will refuse to commit its result;
   *   3. effect run #2 sees the ref already set and returns early — so it never
   *      starts a chain of its own.
   *
   * Nobody is left who is both running and allowed to commit, and `status` stays
   * `'loading'` forever: every page reload sat on "Restoring your session…". It
   * was invisible in tests and invisible in a production build (no StrictMode),
   * which is exactly why it survived review and was only caught by loading the
   * page.
   *
   * The correct shape is the plain one below: let BOTH invocations run and let the
   * cancelled one drop its result. That is safe here precisely because of
   * D-web-1 — run #2's `restoreSession()` joins run #1's in-flight refresh through
   * the single-flight mutex instead of starting a second one, so the two runs still
   * cost exactly ONE `POST /auth/refresh` and cannot trip revoke-on-rotate. The
   * only duplicated work is an idempotent `GET /me`, in development only.
   *
   * `session.boot.test.tsx` asserts the StrictMode double-mount reaches
   * `authenticated` with one refresh, so this cannot regress silently again.
   */
  useEffect(() => {
    // No stored refresh token: `status` was already initialised to 'anonymous' by
    // the same predicate, so there is nothing to set. Setting it here anyway would
    // be a synchronous setState inside an effect — a cascading render for no reason.
    if (!hasStoredSession()) return;
    let cancelled = false;
    void (async () => {
      try {
        await restoreSession();
        const identity = await fetchMe();
        if (cancelled) return;
        setMe(identity);
        setMustChangePassword(identity.must_change_password === true);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        clearTokens();
        setMe(null);
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    await apiLogin(email, password);
    try {
      const identity = await fetchMe();
      setMe(identity);
      // `GET /me` is one of the three routes the must-change gate admits, and its
      // `must_change_password` is the token's own claim: read here, on every sign-in,
      // so a person whose administrator reset their password meets the screen at once.
      setMustChangePassword(identity.must_change_password === true);
      setStatus('authenticated');
    } catch (error) {
      // Credentials were accepted but identity could not be read. Refusing to
      // enter a half-known session is the safe move: every downstream screen
      // depends on scopes and perms, and guessing them is how a CD-only user ends
      // up looking at an IN list.
      clearTokens();
      setMe(null);
      setStatus('anonymous');
      throw error;
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    // `apiLogout` is best-effort: when the caller is premises-blocked the POST
    // itself gets the same 403 (and re-notifies the listener), but the local
    // tokens are cleared regardless. The block flag is dropped AFTER that call
    // returns, so the re-notification cannot resurrect the screen the user is
    // leaving.
    await apiLogout();
    setMe(null);
    setStatus('anonymous');
    setPremisesBlocked(false);
    setMustChangePassword(false);
  }, []);

  /**
   * Design U8 / D16 — the password screen's exit. The server accepted the new password
   * and revoked every refresh token (`AuthService.changePassword`), so the only way
   * forward is a fresh sign-in; doing it here, with the value the person just typed,
   * spares them the login form. `signIn` re-reads `/me`, whose fresh token carries no
   * claim, so the flag clears from the server's own answer rather than from an
   * assumption — a change that somehow still left the claim set would keep the screen up.
   */
  const completePasswordChange = useCallback(
    async (newPassword: string): Promise<void> => {
      const email = me?.email;
      if (email === undefined) {
        throw new Error('completePasswordChange() called with no signed-in identity.');
      }
      await signIn(email, newPassword);
    },
    [me, signIn],
  );

  const accessibleCountries = useMemo(() => accessibleCountriesOf(me), [me]);
  const isGroupAdmin = me?.grp === true;

  const permsIn = useCallback(
    (code: CountryCode): readonly PermissionKey[] => {
      if (me === null) return [];
      const keys = me.perms[code];
      return Array.isArray(keys) ? (keys as PermissionKey[]) : [];
    },
    [me],
  );

  const can = useCallback(
    (code: CountryCode, key: PermissionKey): boolean => {
      if (me === null) return false;
      if (me.grp) return true;
      return permsIn(code).includes(key);
    },
    [me, permsIn],
  );

  const value = useMemo<Session>(
    () => ({
      status,
      me,
      accessibleCountries,
      isGroupAdmin,
      mustChangePassword,
      permsIn,
      can,
      signIn,
      signOut,
      completePasswordChange,
    }),
    [
      status,
      me,
      accessibleCountries,
      isGroupAdmin,
      mustChangePassword,
      permsIn,
      can,
      signIn,
      signOut,
      completePasswordChange,
    ],
  );

  /*
   * THE TWO WHOLE-SESSION SCREENS, in this order: the premises block first (a session
   * off-premises cannot even reach `PATCH /me/password` — the guard bites on use), then the
   * must-change screen for an authenticated session only (an anonymous one has no password
   * to change, and the flag is cleared with the session anyway). Everything else is the app.
   */
  const screen = premisesBlocked ? (
    <PremisesBlockedScreen
      onSignOut={() => {
        void signOut();
      }}
    />
  ) : mustChangePassword && status === 'authenticated' ? (
    <PasswordChangeRequiredScreen
      onChanged={completePasswordChange}
      onSignOut={() => {
        void signOut();
      }}
    />
  ) : (
    children
  );

  return <SessionContext.Provider value={value}>{screen}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error('useSession must be used inside <SessionProvider>.');
  }
  return session;
}

